import { Component, inject } from '@angular/core';
import { ButtonComponent } from "../../component/layout/element/button.component";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { FormsModule } from '@angular/forms';
import { Firestore } from '@angular/fire/firestore';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { v4 as v4uuid } from 'uuid';
import { COLLECTION_PATH_INTERESTED_USERS, InterestedUserEntry } from '../../../../globalTypes/user.types';
import { httpsCallable } from 'firebase/functions';
import { Functions } from '@angular/fire/functions';
import { LoadingLogoComponent } from "../../component/layout/element/loading-logo.component";
import { onCallFunctions } from '../../../../globalTypes/backend.api.types';


@Component({
  selector: 'msh-register-your-interest',
  imports: [MatCheckboxModule, FormsModule, ButtonComponent, LoadingLogoComponent],
  template: `
    
    <div class="hero-image flex flex-col items-center text-center ">
      <img [src]="'MobileStorehouse_logo_landscape-reverse.png'" 
           alt="Mobile Storehouse Logo" 
           class="max-w-xs sm:max-w-lg mb-6 mt-16" />

      <h1 class="text-3xl sm:text-4xl font-bold text-heavy-copy mb-2">
        Mobile Storehouse is coming&nbsp;soon!
      </h1>
      <div class="text-lg text-gray-200 max-w-2xl mx-4 mt-8 mb-32">
        A business to business small wares and appliance marketplace designed for restaurant owners and&nbsp;franchisees.
      </div>
    </div>

    <div class=" flex flex-col items-center text-center px-6 py-12">
      @if(mode === 'submitted'){
        <div class="text-2xl font-bold mb-4">Thank you for registering your interest!</div>
        <div class=" max-w-md">
          We will be in touch with updates about our launch and beta testing opportunities.
        </div>
        <div (click)="setupRegister()" class=" mt-16 lil-action">register an additional user</div>
      } @else if(mode === 'loading'){
        <msh-loading-logo class=" w-96 h-96"></msh-loading-logo>
      } @else {
        <div class=" mb-8">Register your interest to be notified when we launch in your&nbsp;state:</div>
        
        <input type="text"
              [(ngModel)]="name"
              placeholder="Enter your name"
              class="w-full max-w-md border border-gray-300 rounded-md px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
        <input type="email"
              [(ngModel)]="email"
              name="email"
              placeholder="Enter your email"
              class="w-full max-w-md border border-gray-300 rounded-md px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
        <select name="state_in_usa" placeholder="Select your state in the USA"
                [(ngModel)]="state"
                class="w-full bg-back text-copy max-w-md border border-navy/15 rounded-md px-4 py-3 mt-6 mb-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option value="" disabled selected>Select your state in the USA</option>
          @for(state of stateOptions; track state){
            <option [value]="state.code">{{state.name}}</option>
          }
        </select>
        <mat-checkbox [(ngModel)]="interestedInBeta"> I am interested in beta testing for Mobile Storehouse</mat-checkbox> 
          @if(errorMessage){
            <div class=" text-red-500 mt-4">{{errorMessage}}</div>
          }
        <msh-button
          cta="Register Your Interest"
          [disabled]="!name || !email"
          (onClick)="register()"
          class="mt-8"
          >
        </msh-button>
        <div class=" max-w-80 mt-16 text-sm opacity-50">By submitting your details you are agreeing to being contacted by Mobile Storehouse {{interestedInBeta ? 'when the site is ready for beta testing':'when the site is live'}}.</div>
      }
    </div>
  `,
  styles: `
  .hero-image {position: relative; z-index: 1; background-color: black; }
  .hero-image::before {
  content: ""; /* Required for pseudo-elements */
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('public/hero-image.jpg');
  background-position: center;
  background-size: cover; 
  opacity: 0.4; 
  z-index: -1; /* Puts the pseudo-element behind the content */ 
  }
  `
})
export class RegisterYourInterestComponent {

    private firestore: Firestore = inject(Firestore); // inject Cloud Firestore
  mode: "register" | "submitted" | "loading" = "register";
  name = "";
  email = "";
  state = "";
  errorMessage = "";
  interestedInBeta = false;
  stateOptions = [
    { name: 'Alabama', code: 'AL' },
    { name: 'Alaska', code: 'AK' },
    { name: 'Arizona', code: 'AZ' },
    { name: 'Arkansas', code: 'AR' },
    { name: 'California', code: 'CA' },
    { name: 'Colorado', code: 'CO' },
    { name: 'Connecticut', code: 'CT' },
    { name: 'Delaware', code: 'DE' },
    { name: 'Florida', code: 'FL' },
    { name: 'Georgia', code: 'GA' },
    { name: 'Hawaii', code: 'HI' },
    { name: 'Idaho', code: 'ID' },
    { name: 'Illinois', code: 'IL' },
    { name: 'Indiana', code: 'IN' },
    { name: 'Iowa', code: 'IA' },
    { name: 'Kansas', code: 'KS' },
    { name: 'Kentucky', code: 'KY' },
    { name: 'Louisiana', code: 'LA' },
    { name: 'Maine', code: 'ME' },
    { name: 'Maryland', code: 'MD' },
    { name: 'Massachusetts', code: 'MA' },
    { name: 'Michigan', code: 'MI' },
    { name: 'Minnesota', code: 'MN' },
    { name: 'Mississippi', code: 'MS' },
    { name: 'Missouri', code: 'MO' },
    { name: 'Montana', code: 'MT' },
    { name: 'Nebraska', code: 'NE' },
    { name: 'Nevada', code: 'NV' },
    { name: 'New Hampshire', code: 'NH' },
    { name: 'New Jersey', code: 'NJ' },
    { name: 'New Mexico', code: 'NM' },
    { name: 'New York', code: 'NY' },
    { name: 'North Carolina', code: 'NC' },
    { name: 'North Dakota', code: 'ND' },
    { name: 'Ohio', code: 'OH' },
    { name: 'Oklahoma', code: 'OK' },
    { name: 'Oregon', code: 'OR' },
    { name: 'Pennsylvania', code: 'PA' },
    { name: 'Rhode Island', code: 'RI' },
    { name: 'South Carolina', code: 'SC' },
    { name: 'South Dakota', code: 'SD' },
    { name: 'Tennessee', code: 'TN' },
    { name: 'Texas', code: 'TX' },
    { name: 'Utah', code: 'UT' },
    { name: 'Vermont', code: 'VT' },
    { name: 'Virginia', code: 'VA' },
    { name: 'Washington', code: 'WA' },
    { name: 'West Virginia', code: 'WV' },
    { name: 'Wisconsin', code: 'WI' },
    { name: 'Wyoming', code: 'WY' }
  ];

  constructor(
    private functions: Functions,
  ){}
  async register() {
    console.log("Registering interest for:", this.name, this.email, this.state);  
    this.mode = 'loading';
    // Simulate an async operation like an HTTP request
    const collectUserRef = collection(this.firestore, COLLECTION_PATH_INTERESTED_USERS);
    const uid = v4uuid();
    const stateName = this.stateOptions.find(s=>s.code === this.state)?.name || "";
    const userEntry: InterestedUserEntry = {
      uid,
      name: this.name,
      email: this.email,
      stateCode: this.state,
      stateName,
      dateRegistered: new Date(),
      interestedInBeta: this.interestedInBeta,
    }
    try {
      const user = await setDoc(doc(collectUserRef, uid), userEntry);
    } catch(error){
      this.errorMessage = "There was an error registering your interest. Please try again.";
      console.error("Error registering interest:", error);
      this.mode = 'register';
      return;
    }
    try {
      const callable = httpsCallable(this.functions,  onCallFunctions.registerInterest);
      const result = await callable({uid});
      console.log("Register interest function result:", result.data); 
    } catch(error){
      console.error("Error calling registerInterest function:", error);
      this.errorMessage = "There was an error registering your interest. Please try again.";
      this.mode = 'register';
      return;
    }
    this.mode = 'submitted';
  };

  setupRegister(){
    this.name = "";
    this.email = "";
    this.state = "";
    this.interestedInBeta = false;
    this.mode = "register";
    this.errorMessage = "";
  }
}
